// A streamed Mac window's pictures: the `format` frame configures a WebCodecs decoder on the link, every `frame`
// after it is an H.264 sample in AVCC form (a key flag, a presentation time, the sample), decoded into
// `link.onFrame`. Registered for those two kinds by index.js; the link (core/wire.js MacWindows) keeps the decoder.
const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0))

/// `format`: the parameter sets sent first, which is exactly what WebCodecs wants — an avcC record to configure
/// with, then the samples as they are.
export function configure(link, format) {
  if (!format || !window.VideoDecoder) { link.onFailure(format ? 'This browser cannot decode video (no WebCodecs).' : 'Bad format.'); return }
  link.format = format
  link.onFormat(format)
  const [sps, pps] = (format.parameterSets || []).map(fromB64)
  if (!sps || !pps) { link.onFailure('The Mac sent no parameter sets.'); return }
  // the avcC record: version, profile, compat, level, 0xFF (4-byte lengths), one SPS, one PPS
  const avcC = new Uint8Array(11 + sps.length + pps.length)
  avcC.set([1, sps[1], sps[2], sps[3], 0xff, 0xe1, sps.length >> 8, sps.length & 0xff], 0)
  avcC.set(sps, 8)
  avcC.set([1, pps.length >> 8, pps.length & 0xff], 8 + sps.length)
  avcC.set(pps, 11 + sps.length)
  const hex = (n) => n.toString(16).padStart(2, '0')
  const codec = `avc1.${hex(sps[1])}${hex(sps[2])}${hex(sps[3])}`
  link.decoder?.close()
  link.decoder = new VideoDecoder({
    output: (frame) => { link.onFrame(frame); frame.close() },
    error: (e) => { console.warn('decoder error: ' + e.message); link.onFailure('Decoder: ' + e.message); link.keyframe() },
  })
  link.decoder.configure({ codec, description: avcC, optimizeForLatency: true })
  link.awaitingKey = true
}

/// `frame`: 1 byte flags · 8 bytes pts ms · the AVCC sample. Nothing is decoded until a keyframe has come.
export function decode(link, payload) {
  if (!link.decoder || link.decoder.state !== 'configured') return
  const key = (payload[0] & 1) === 1
  if (link.awaitingKey && !key) return
  link.awaitingKey = false
  const pts = Number(new DataView(payload.buffer, payload.byteOffset + 1, 8).getBigUint64(0))
  try {
    link.decoder.decode(new EncodedVideoChunk({ type: key ? 'key' : 'delta', timestamp: pts * 1000, data: payload.subarray(9) }))
  } catch (e) { link.onFailure('Decode: ' + e.message); link.awaitingKey = true; link.keyframe() }
}
